// Shared client-side QR rendering for the free /tools/ generator widgets
// (generator, wifi, vcard). Encoding is done with qrcode-generator (tiny,
// zero-dependency); we read its module matrix and emit our own crisp SVG and
// a canvas-rendered PNG so downloads stay sharp at any print size.
//
// Everything here runs in the browser — no network, nothing uploaded. The
// codes produced are STATIC (the data is baked into the image); that's the
// point of these top-of-funnel tools, and every page upsells the editable
// product. See app/models/tools.rb.
import qrcode from "qrcode-generator"

// qrcode-generator's default stringToBytes is Latin1 (charCode & 0xff). Feed it
// a binary string whose char codes are the UTF-8 bytes, so accented names and
// other multibyte text encode correctly for real scanners.
function utf8Binary(text) {
  return Array.from(new TextEncoder().encode(text), (b) => String.fromCharCode(b)).join("")
}

// Build a QR model at the highest error-correction level (matches the product's
// default), auto-sizing the version to fit. Throws on overflow (data too long).
export function makeQr(text) {
  const qr = qrcode(0, "H")
  qr.addData(utf8Binary(text))
  qr.make()
  return qr
}

// A crisp, scalable SVG string. margin is the quiet zone in modules (4 = spec).
export function toSvg(qr, { fg = "#111111", bg = "#ffffff", margin = 4 } = {}) {
  const count = qr.getModuleCount()
  const size = count + margin * 2
  let rects = ""
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) rects += `<rect x="${c + margin}" y="${r + margin}" width="1" height="1"/>`
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
    `<rect width="${size}" height="${size}" fill="${bg}"/>` +
    `<g fill="${fg}">${rects}</g></svg>`
  )
}

// A PNG data URL, scaled up to roughly `pixels` on a side for clean printing.
export function toPngDataUrl(qr, { fg = "#111111", bg = "#ffffff", margin = 4, pixels = 1024 } = {}) {
  const count = qr.getModuleCount()
  const size = count + margin * 2
  const scale = Math.max(1, Math.floor(pixels / size))
  const dim = size * scale
  const canvas = document.createElement("canvas")
  canvas.width = dim
  canvas.height = dim
  const ctx = canvas.getContext("2d")
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, dim, dim)
  ctx.fillStyle = fg
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (qr.isDark(r, c)) ctx.fillRect((c + margin) * scale, (r + margin) * scale, scale, scale)
    }
  }
  return canvas.toDataURL("image/png")
}

// Trigger a download of a data/object URL.
export function downloadUrl(filename, url) {
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// Trigger a download of an in-memory text blob (e.g. the SVG string).
export function downloadText(filename, text, mime = "image/svg+xml") {
  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  downloadUrl(filename, url)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
